import { AudioElement, ContentPropertiesManager, ImageElement, VideoElement } from '@bynder-studio/render-core';

export const isImageOrVideo = (type) => ['image', 'video'].includes(type.toLowerCase());
export const elementIsVideo = (type) => ['video'].includes(type.toLowerCase());

type FieldsToChange = {
    fileName: string;
    offsetTime: number;
    src: string;
    srcId: string;
    srcType: string;
    externalId: string;
    naturalDimension?: {
        width: number;
        height: number;
    };
    duration?: number;
    isAlpha?: boolean;
    contentTransform?: {
        type: string;
        scale?: number;
        horizontalOffset?: number;
        verticalOffset?: number;
        verticalAlignment?: number;
        horizontalAlignment?: number;
    };
};

export const getElementPropertiesFromSource = (
    element: ImageElement | VideoElement | AudioElement,
    mediaType: 'IMAGE' | 'VIDEO' | 'AUDIO',
    source,
): FieldsToChange => {
    if (element instanceof AudioElement) {
        const { name: fileName, offsetTime, id: srcId, type: srcType = 'ASSET_MANAGER', url, duration } = source;

        return {
            fileName,
            offsetTime,
            duration,
            src: url,
            srcId,
            srcType,
        } as FieldsToChange;
    }

    const { width: naturalWidth, height: naturalHeight } = element.naturalDimension;
    const { type: cropMode, scale, horizontalOffset, verticalOffset } = element.contentTransform;

    let newScale;

    const {
        name: fileName,
        offsetTime,
        id: srcId,
        type: srcType = 'ASSET_MANAGER',
        src,
        duration,
        width,
        height,
        videoPreviewUrl,
        isAlpha,
        externalId,
    } = source;

    if (cropMode === 'FREE') {
        newScale = Number(
            (naturalWidth > naturalHeight ? (naturalWidth / width) * scale : (naturalHeight / height) * scale).toFixed(
                2,
            ),
        );
        newScale = newScale > 2 ? 2 : newScale <= 0 ? 0 : newScale;
    }

    const fieldsToChange: FieldsToChange = {
        fileName,
        offsetTime,
        src: videoPreviewUrl || src,
        srcId,
        srcType,
        externalId,
        naturalDimension: {
            width,
            height,
        },
    };

    if (mediaType === 'VIDEO') {
        fieldsToChange.duration = duration;
        fieldsToChange.isAlpha = isAlpha;
    }

    if (cropMode === 'FREE') {
        fieldsToChange.contentTransform = {
            type: 'FREE',
            scale: newScale,
            horizontalOffset: horizontalOffset - Math.round((width * newScale - naturalWidth * scale) / 2),
            verticalOffset: verticalOffset - Math.round((height * newScale - naturalHeight * scale) / 2),
        };
    }

    return fieldsToChange;
};

export const getFreeCropModeParams = (element) => {
    const { naturalDimension, dimension } = element;
    const { width: naturalWidth, height: naturalHeight } = naturalDimension;
    const { width, height } = dimension;
    const scale = Number(Math.min(width / naturalWidth, height / naturalHeight, 1).toFixed(2));
    const horizontalOffset = Math.round(width / 2 - (naturalWidth * scale) / 2);
    const verticalOffset = Math.round(height / 2 - (naturalHeight * scale) / 2);

    return {
        scale,
        horizontalOffset,
        verticalOffset,
    };
};

export const getResetContentTransformOnSourceChange = (element, naturalDimension) => {
    if (!naturalDimension || !element?.contentTransform) {
        return undefined;
    }

    const { type } = element.contentTransform;
    const { width: naturalWidth, height: naturalHeight } = naturalDimension;
    const { width, height } = element.dimension;

    if (type === 'FREE') {
        const scale = Number(Math.min(width / naturalWidth, height / naturalHeight, 1).toFixed(2));

        return {
            type,
            scale,
            horizontalOffset: Math.round(width / 2 - (naturalWidth * scale) / 2),
            verticalOffset: Math.round(height / 2 - (naturalHeight * scale) / 2),
        };
    }

    // For non-FREE modes: preserve alignment, include the auto-calculated
    // scale so the backend stores the correct value for the new source.
    // Without an explicit scale, the backend keeps the old value which
    // causes incorrect rendering after variation switches.
    const getDerivedScale = () => {
        switch (type) {
            case 'COVER':
                return Math.max(width / naturalWidth, height / naturalHeight);
            case 'COVER_WIDTH':
                return width / naturalWidth;
            case 'COVER_HEIGHT':
                return height / naturalHeight;
            case 'CONTAIN':
                return Math.min(width / naturalWidth, height / naturalHeight);
            default:
                return undefined;
        }
    };

    return {
        type,
        verticalAlignment: element.contentTransform.verticalAlignment ?? 0,
        horizontalAlignment: element.contentTransform.horizontalAlignment ?? 0,
        scale: getDerivedScale(),
    };
};

export const syncLinkedContentPropertyTransforms = ({
    creativeModel,
    sourceElement,
    contentTransform,
    naturalDimension,
    onLinkedElementUpdated,
}: {
    creativeModel: any;
    sourceElement: any;
    contentTransform: any;
    naturalDimension?: { width: number; height: number };
    onLinkedElementUpdated?: (modelIndex: number, linkedElementId: number, linkedParams: any) => void;
}) => {
    if (!sourceElement?.contentPropertyId || !contentTransform) {
        return;
    }

    const models = creativeModel.getModels();

    models.forEach((model, modelIndex) => {
        model.getAllElementsRecursively().forEach((linkedElement) => {
            if (
                linkedElement.id === sourceElement.id ||
                linkedElement.contentPropertyId !== sourceElement.contentPropertyId
            ) {
                return;
            }

            const allowedPropsToChange = ContentPropertiesManager.getSyncedPropertiesList(linkedElement, [
                'contentTransform',
            ]);

            if (!allowedPropsToChange.includes('contentTransform')) {
                return;
            }

            // Preserve each linked element's own type and alignment
            // since different pages may have different crop modes and positioning
            // for the same content property.
            let linkedContentTransform = contentTransform;

            if (linkedElement.contentTransform) {
                const linkedType = linkedElement.contentTransform.type;

                if (linkedType !== 'FREE') {
                    // For non-FREE modes, preserve the linked element's own type and alignment;
                    // include the derived scale for the new source so the backend stores
                    // the correct value (otherwise the old scale persists).
                    const natDim = naturalDimension || sourceElement.naturalDimension;
                    let linkedScale;

                    if (linkedElement.dimension && natDim?.width && natDim?.height) {
                        const { width, height } = linkedElement.dimension;
                        const natW = natDim.width;
                        const natH = natDim.height;
                        switch (linkedType) {
                            case 'COVER':
                                linkedScale = Math.max(width / natW, height / natH);
                                break;
                            case 'COVER_WIDTH':
                                linkedScale = width / natW;
                                break;
                            case 'COVER_HEIGHT':
                                linkedScale = height / natH;
                                break;
                            case 'CONTAIN':
                                linkedScale = Math.min(width / natW, height / natH);
                                break;
                        }
                    }

                    linkedContentTransform = {
                        type: linkedType,
                        horizontalAlignment: linkedElement.contentTransform.horizontalAlignment ?? 0,
                        verticalAlignment: linkedElement.contentTransform.verticalAlignment ?? 0,
                        ...(linkedScale !== undefined ? { scale: linkedScale } : {}),
                    };
                } else if (contentTransform.type !== 'FREE') {
                    // Source is non-FREE but linked element is FREE — preserve FREE mode,
                    // keep existing offsets/scale (will be recalculated by the renderer
                    // when the new image source loads).
                    linkedContentTransform = undefined;
                }
                // For FREE→FREE, use the source's recalculated contentTransform as before
            }

            if (!linkedContentTransform) {
                return;
            }

            const linkedParams = { contentTransform: linkedContentTransform };

            model.updateElement(linkedElement.id, linkedParams);
            onLinkedElementUpdated?.(modelIndex, linkedElement.id, linkedParams);
        });
    });
};

export const getScaleValueFromElement = (element) => {
    const { naturalDimension, dimension, contentTransform } = element;
    const { width: naturalWidth, height: naturalHeight } = naturalDimension;
    const { width, height } = dimension;

    if (!contentTransform) {
        return Number(Math.min(width / naturalWidth, height / naturalHeight, 1).toFixed(2));
    }

    if (contentTransform.hasManualScale?.()) {
        return contentTransform.scale;
    }

    const type = contentTransform.type ?? contentTransform.getType?.();

    switch (type) {
        case 'COVER':
            return Math.max(width / naturalWidth, height / naturalHeight);
        case 'COVER_WIDTH':
            return width / naturalWidth;
        case 'COVER_HEIGHT':
            return height / naturalHeight;
        case 'CONTAIN':
            return Math.min(width / naturalWidth, height / naturalHeight);
        default:
            return (
                contentTransform.scale ?? Number(Math.min(width / naturalWidth, height / naturalHeight, 1).toFixed(2))
            );
    }
};

export const getHorizontalOffsetValueFromElement = (element) => {
    const { naturalDimension, dimension, contentTransform } = element;
    const { width: naturalWidth } = naturalDimension;
    const { width } = dimension;
    const scale = getScaleValueFromElement(element);
    const { horizontalOffset = Math.round(width / 2 - (naturalWidth * scale) / 2) } = contentTransform || {};

    return horizontalOffset;
};

export const getVerticalOffsetValueFromElement = (element) => {
    const { naturalDimension, dimension, contentTransform } = element;
    const { height: naturalHeight } = naturalDimension;
    const { height } = dimension;
    const scale = getScaleValueFromElement(element);
    const { verticalOffset = Math.round(height / 2 - (naturalHeight * scale) / 2) } = contentTransform || {};

    return verticalOffset;
};

const DROP_SHADOW_DEFAULT_VALUE = {
    offset: 5,
    angle: 140,
    blurRadius: 5,
    spreadRadius: null,
    color: { type: 'SOLID', red: 0, green: 0, blue: 0, opacity: 0.25, brandColorId: null },
    state: 'DISABLE',
};

export const getDropShadowValueFromElement = (element) => {
    const dropShadowValue = element.dropShadow || DROP_SHADOW_DEFAULT_VALUE;
    const { color } = dropShadowValue;

    return {
        ...dropShadowValue,
        color,
    };
};

export const MAX_ELEMENT_NAME_LENGTH = 128;
